// frontend/src/modules/esign/components/SignatureModal.tsx
//
// Modal used by the public signer page (Task 9) to capture a signature or
// initials image. Three ways in: draw on a canvas, type a name (rendered in
// 'Instrument Serif' onto an offscreen canvas), or upload a PNG/JPEG (redrawn
// through a canvas so the output is always a PNG data URL, capped at 700KB
// client-side to match the backend's MAX_IMAGE_BYTES in public.routes.ts).
import * as React from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { cn } from '../../../lib/utils';
import type { EsignSignatureKind } from '../types';

export interface SignatureResult {
  kind: EsignSignatureKind;
  dataUrl: string;
}

interface SignatureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCapture: (result: SignatureResult) => void;
  /** Smaller footprint for initials. Defaults to full signature size. */
  compact?: boolean;
  title?: string;
}

const MAX_BYTES = 700 * 1024;

type Tab = 'draw' | 'type' | 'upload';

/** Rough base64 → byte-length estimate, good enough for a client-side cap. */
function dataUrlByteLength(dataUrl: string): number {
  const b64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  return Math.floor((b64.length * 3) / 4);
}

export function SignatureModal({ open, onOpenChange, onCapture, compact, title }: SignatureModalProps) {
  const [tab, setTab] = React.useState<Tab>('draw');
  const [typedName, setTypedName] = React.useState('');
  const [uploadDataUrl, setUploadDataUrl] = React.useState<string | null>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const drawing = React.useRef(false);
  const hasDrawn = React.useRef(false);

  const width = compact ? 220 : 420;
  const height = compact ? 90 : 140;

  React.useEffect(() => {
    if (!open) return;
    setTab('draw');
    setTypedName('');
    setUploadDataUrl(null);
    hasDrawn.current = false;
    // Clear the draw canvas on open (it may be reused across calls).
    requestAnimationFrame(() => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    });
  }, [open]);

  function pointerPos(e: React.PointerEvent<HTMLCanvasElement>) {
    const canvas = e.currentTarget;
    const rect = canvas.getBoundingClientRect();
    // The backing store is a fixed `width`/`height` while the element renders
    // at `w-full`, so CSS pixels must be mapped into backing-store pixels —
    // without this the ink drifts right of the pen and the right edge of the
    // pad is unreachable.
    const scaleX = rect.width > 0 ? canvas.width / rect.width : 1;
    const scaleY = rect.height > 0 ? canvas.height / rect.height : 1;
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  }

  function handlePointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    drawing.current = true;
    const { x, y } = pointerPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  }

  function handlePointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const { x, y } = pointerPos(e);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#111';
    ctx.lineTo(x, y);
    ctx.stroke();
    hasDrawn.current = true;
  }

  function handlePointerUp() {
    drawing.current = false;
  }

  function handleClear() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasDrawn.current = false;
  }

  function handleUploadChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
      toast.error('Please upload a PNG or JPEG image');
      return;
    }
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => {
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL('image/png');
        if (dataUrlByteLength(dataUrl) > MAX_BYTES) {
          toast.error('Image is too large — please use a file under 700KB');
          return;
        }
        setUploadDataUrl(dataUrl);
      };
      img.src = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  function handleUse() {
    if (tab === 'draw') {
      const canvas = canvasRef.current;
      if (!canvas || !hasDrawn.current) {
        toast.error('Please draw your signature first');
        return;
      }
      const dataUrl = canvas.toDataURL('image/png');
      if (dataUrlByteLength(dataUrl) > MAX_BYTES) {
        toast.error('Signature image is too large');
        return;
      }
      onCapture({ kind: 'drawn', dataUrl });
      onOpenChange(false);
      return;
    }
    if (tab === 'type') {
      const name = typedName.trim();
      if (!name) {
        toast.error('Please type your name');
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width = width * 2;
      canvas.height = height * 2;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.scale(2, 2);
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = '#111';
      ctx.font = "italic 32px 'Instrument Serif', cursive";
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';
      ctx.fillText(name, width / 2, height / 2);
      const dataUrl = canvas.toDataURL('image/png');
      onCapture({ kind: 'typed', dataUrl });
      onOpenChange(false);
      return;
    }
    if (tab === 'upload') {
      if (!uploadDataUrl) {
        toast.error('Please choose an image to upload');
        return;
      }
      onCapture({ kind: 'uploaded', dataUrl: uploadDataUrl });
      onOpenChange(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title ?? 'Add your signature'}</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-1.5 mb-4">
          {(['draw', 'type', 'upload'] as Tab[]).map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={cn(
                'rounded-full px-3 py-1.5 text-[12.5px] font-medium ring-1 transition-colors capitalize',
                tab === t
                  ? 'bg-coral text-white ring-coral'
                  : 'bg-white/70 dark:bg-white/[0.04] ring-ink/8 dark:ring-white/10 text-ink dark:text-white',
              )}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === 'draw' && (
          <div className="space-y-3">
            <canvas
              ref={canvasRef}
              width={width}
              height={height}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerLeave={handlePointerUp}
              className="w-full rounded-xl bg-white ring-1 ring-ink/10 touch-none"
              style={{ height }}
              role="img"
              aria-label="Signature drawing area"
            />
            <Button type="button" variant="outline" size="sm" onClick={handleClear}>
              Clear
            </Button>
          </div>
        )}

        {tab === 'type' && (
          <div className="space-y-3">
            <Input
              autoFocus
              placeholder="Type your name"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              aria-label="Type your name"
            />
            {typedName.trim() && (
              <div
                className="w-full rounded-xl bg-white ring-1 ring-ink/10 flex items-center justify-center"
                style={{ height, fontFamily: "'Instrument Serif', cursive", fontStyle: 'italic', fontSize: 32 }}
              >
                {typedName}
              </div>
            )}
          </div>
        )}

        {tab === 'upload' && (
          <div className="space-y-3">
            <input
              type="file"
              accept="image/png,image/jpeg"
              onChange={handleUploadChange}
              aria-label="Upload signature image"
            />
            {uploadDataUrl && (
              <img src={uploadDataUrl} alt="Uploaded signature preview" style={{ height, objectFit: 'contain' }} />
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" onClick={handleUse}>
            Use this
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
